import React, {useEffect, useRef, useState} from 'react';
import {Button, Flex, Text, useToast} from '@chakra-ui/react';
import axios from 'axios';

type Props = {url: string; group: 'verified' | 'unverified'; q: string; filter: string; filters?: Record<string, string>; disabled?: boolean};
export default function ApprovalExport(props: Props) {
  // Filter or page-group changes cancel downloads for the previous selection.
  return <ExportButtons key={JSON.stringify([props.group, props.q, props.filter, props.filters])} {...props} />;
}
function ExportButtons({url, group, q, filter, filters, disabled}: Props) {
  const pending = useRef<AbortController | null>(null);
  const [busy, setBusy] = useState<'csv' | 'xlsx' | null>(null);
  const toast = useToast();
  useEffect(() => () => {pending.current?.abort();}, []);
  async function download(format: 'csv' | 'xlsx') {
    if (pending.current || disabled) return;
    const controller = new AbortController();
    pending.current = controller; setBusy(format);
    try {
      const response = await axios.get(`${url}/export`, {params: {...filters, group, q, filter, format},
        signal: controller.signal, responseType: 'blob', timeout: 120000, withCredentials: true});
      if (controller.signal.aborted) return;
      const type = format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      if (!String(response.headers['content-type']).includes(type)) throw new Error('Invalid export');
      const objectUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `Pureworker_${group === 'verified' ? 'Verified' : 'Unverified'}_SPs.${format}`;
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
      toast({title: `${group === 'verified' ? 'Verified' : 'Unverified'} SPs export downloaded`, status: 'success', duration: 4000, isClosable: true});
    } catch {
      if (!controller.signal.aborted) toast({title: 'Export failed', description: 'No complete file was downloaded. Try again or sign in again if your session expired.', status: 'error', duration: 8000, isClosable: true});
    } finally {
      if (!controller.signal.aborted) {pending.current = null; setBusy(null);}
    }
  }
  return <>
    <Flex gap={2} wrap="wrap" mb={2}>
      <Button colorScheme="purple" onClick={() => download('csv')} isDisabled={disabled || !!busy} isLoading={busy === 'csv'} loadingText="Exporting CSV">Export all matching CSV (SMS)</Button>
      <Button colorScheme="purple" variant="outline" onClick={() => download('xlsx')} isDisabled={disabled || !!busy} isLoading={busy === 'xlsx'} loadingText="Exporting Excel">Export all matching Excel</Button>
    </Flex>
    <Text fontSize="sm" mb={4}>Exports every provider matching this page's search and filters, across all pages. Includes registration date, both approval statuses and verification dates. Nigerian phone numbers use 080…, 090… format; Excel preserves the leading zero. Leaving this page or changing filters cancels a download in progress.</Text>
  </>;
}
